// @ts-strict-ignore
import { Directive, Input, ViewChild } from '@angular/core';
import {
  LMListEventService,
  LMListGridBuilderService,
  LMListSpecUtilService,
  LMListUtilService,
} from './services';
import { LMEditorService } from './editors';
import { LMFormatterService } from './formatters';
import { LMUrlUtilService } from '@services/utils/lm-url-util.service';
import { LMFormatUtilService } from '@services/utils/lm-format-util.service';
import { AppConstants, LMDialogButtonKey, LMTransformOptions } from '@utils/app.constants';
import { LMLoggerService } from '@services/lm-logger.service';
import { SohoBusyIndicatorDirective, SohoDataGridComponent, SohoMessageService } from 'ids-enterprise-ng';
import { LMListContentBaseComponent } from './lm-list-content-base.component';
import { LMConditionUtilService } from '@utils/lm-condition-util.service';
import { LMCompoundFieldService } from '@services/lm-compound-field.service';
import { LMUniqueIdService } from '@utils/lm-unique-id.service';
import { LMThemeService } from '@banner/lm-theme.service';
import { LMContextService } from '@app/services';
import { SohoDataGridKeyDownEvent } from 'ids-enterprise-ng/lib/datagrid/soho-datagrid.component';
import { isEqual } from 'lodash-es';

@Directive() // tslint:disable-next-line:directive-class-suffix
// eslint-disable-next-line @angular-eslint/directive-class-suffix
export abstract class LMDataGridBaseComponent extends LMListContentBaseComponent {
  /**
   * Only load data if filterCriteria or filterListFilterCriteria are set.
   */
  @Input() hasFilterList: boolean;
  @Input() set showFilter(showFilter: boolean) {
    this.toggleFilterRow();
  }

  @ViewChild(SohoBusyIndicatorDirective) sohoBusyIndicator: SohoBusyIndicatorDirective;
  @ViewChild('SohoDataGrid') sohoDataGridComponent: SohoDataGridComponent;

  gridOptions: SohoDataGridOptions;
  savedColumns: ListGridColumn[];

  protected allowUpdate = false;
  protected hasDisplayFields = false;
  protected hasMultiLineTextArea = false;
  // protected nbrMultiLineTextAreaLines = 0;
  // protected maxNbrMultiLineTextAreaLines = 3;
  protected showFilterMenuItemEnabled = true;
  protected allowMultiSelections = false;
  protected allowMaintainingMultiSelections = false;
  protected selectedValuesData: SelectedValuesData;
  protected doNotClearSelectionTags = false;

  protected disableSelectionListener = false;

  protected selectionInProgress = false;

  protected testVersion1: boolean;

  public dataGridAutomationId: string;

  protected timeZoneDependentFields: LMTimeZoneDependentField[] = [];

  protected constructor(
    protected lmConditionUtilService: LMConditionUtilService,
    protected lmContextService: LMContextService,
    protected lmEditorService: LMEditorService,
    protected lmFormatterService: LMFormatterService,
    protected lmFormatUtilService: LMFormatUtilService,
    protected lmListEventService: LMListEventService,
    protected lmListGridBuilderService: LMListGridBuilderService,
    protected lmLoggerService: LMLoggerService,
    protected lmThemeService: LMThemeService,
    protected lmUrlUtilService: LMUrlUtilService,
    protected lmListSpecUtilService: LMListSpecUtilService,
    protected lmListUtilService: LMListUtilService,
    protected lmCompoundFieldService: LMCompoundFieldService,
    protected sohoMessageService: SohoMessageService,
    protected lmUniqueIdService: LMUniqueIdService
  ) {
    super(lmLoggerService, lmThemeService, lmFormatUtilService, lmListUtilService, lmConditionUtilService);
    this.testVersion1 = this.lmContextService.testVersion() === AppConstants.TESTVERSION_V1;
  }

  protected build(): void {
    this.initialize();
    this.buildGridOptions();
  }

  /**
   * Called at the end of buildGridOptions to give the
   * implementing class a chance to update the grid options
   * object before creating the data grid.
   */
  public abstract updateGridOptions(): void;

  public initialize() {
    this.showFilterMenuItemEnabled = this.listSpec.listType !== 'multilist';
    this.allowUpdate = this.lmListSpecUtilService.allowUpdate(this.listSpec);
    this.hasDisplayFields = this.lmListSpecUtilService.hasDisplayFields(this.listSpec);
    this.allowMultiSelections = this.selectRequestData?.allowMultiSelections || false;
    this.allowMaintainingMultiSelections = this.selectRequestData?.maintainSelectedValues || false;
    this.selectedValuesData = this.selectRequestData?.selectedValuesData;
  }

  public buildGridOptions(): void {
    // -------------------------------------------------------------
    // build grid options
    // make sure context and listSpec are set for the grid options builder
    // -------------------------------------------------------------
    this.lmEditorService.listSpec = this.listSpec;
    this.lmFormatterService.listSpec = this.listSpec;

    // -------------------------------------------------------------
    // assigning the grid options variable should cause the datagrid
    // component to be created. see lm-list-grid.component.html
    // -------------------------------------------------------------
    this.gridOptions = this.lmListGridBuilderService.buildDataGridOptions(this.listUrlInfo, this.listSpec, this.listState, this.listPersonalization, this.allowMultiSelections);

    // ----------------------------------------------------------------
    // Used by grid formatters (and editors?) to get the dataViewSet
    // ----------------------------------------------------------------
    this.gridOptions.userObject = () => this;

    if (this.listSpec?.busListSpec?.expandableRowCardView !== undefined) {
      this.gridOptions.expandableRow = true;
      this.gridOptions.onExpandRow = this.onExpandableRow();
    }

    this.gridOptions.columns
      .map(column => <ListGridColumn> column)
      .forEach((column: ListGridColumn) => {

        column.automationId = this.listSpec.busListSpec.automationId;

      // ----------------------------------------------------------------
      // if selection is disable run the rowClick event emitter
      // when clicking in a cell that's not a checkbox, link, or button
      // ----------------------------------------------------------------
        if (column.isActionable) {
          column.click = (event: MouseEvent, args: SohoDataGridColumnClickData[] ) => this.onColumnClicked(column, event, args);

          // Todo: Button menu not supported yet in datagrid
          // if (column.displayField.buttonMenu) {
          //   setTimeout((event, args) => {
          //     const buttonMenu = $(`.lm-card-view`).find('.lm-card-button-menu-container').children('.popupmenu-wrapper');
          //     buttonMenu.each((index, element) => {
          //       $(element).find('li').on('click', () => {
          //         this.onColumnClicked(column, event, args);
          //       });
          //     });
          //   }, 1000);
          // }
        }

        if (column.displayField?.type === AppConstants.TIMESTAMP && column.displayField?.timeZoneIs) {
          const timeZoneFieldId = column.displayField?.timeZoneIs;
          const timeStampFieldId = column.displayField?.fieldId;
          const dependentField: LMTimeZoneDependentField = this.timeZoneDependentFields.find(fields => fields[timeZoneFieldId]);

          if (dependentField) {
            dependentField[timeZoneFieldId].push(timeStampFieldId);
          } else {
            const timeZoneDependentField: LMTimeZoneDependentField = {};
            timeZoneDependentField[timeZoneFieldId] = [];
            timeZoneDependentField[timeZoneFieldId].push(timeStampFieldId);
            this.timeZoneDependentFields.push(timeZoneDependentField);
          }
        }
      });

    this.updateGridOptions();
  }

  /***********************************************************************
   * EVENT HANDLERS
   ***********************************************************************/
  public onEnterEditMode(event: SohoDataGridEditModeEvent) {
    if (!event) {
      return;
    }

    const columns = this.sohoDataGridComponent.getColumns();
    const columnDef = columns[event.cell];

    switch (columnDef.displayField.type) {
      case AppConstants.PERCENT:
      case AppConstants.NUMERIC:
      case AppConstants.BCD:
        if (this.listSpec.busListSpec.displayAmountRelative
          || this.listSpec.busListSpec.displayNegativeParens) {
          const row = event.row;
          const dataViewSet: LMDataViewSet = this.getDataViewSet();
          const dataView: LMDataView = dataViewSet.data[row];
          const dvField: LMField = dataView.fields[columnDef.fieldId] ? dataView.fields[columnDef.fieldId] : dataView.dataView.fields[columnDef.fieldId];
          const value: string = this.lmFormatUtilService.toDisplayValueWithSpec(dvField.value, columnDef.displayField, { precision: dvField.precision || 0 });

          event.editor.val(value);
        }
      // eslint-disable-next-line no-fallthrough
      default:
        break;
    }
  }

  public onRowSelected(event: SohoDataGridSelectedEvent) {
    if (this.disableSelectionListener) {
      return;
    }

    this.lmListEventService.listRowSelectedEvent({
      event: event.e,
      rows: event.rows as LMListRowData[],
      type: event.type,
      rowData: event.rowData,
      doNotClearSelectionTags: this.doNotClearSelectionTags
    });

    this.doNotClearSelectionTags = false;
  }

  public onRowActivated(event: SohoDataGridRowActivated) {
    this.lmListEventService.listRowActivatedEvent(this.toListRowActivatedEvent(event));
  }

  public onRowDeactivated(event: SohoDataGridRowActivated) {
    this.lmListEventService.listRowDeactivatedEvent(this.toListRowActivatedEvent(event));
  }

  private toListRowActivatedEvent(event: SohoDataGridRowActivated): LMListRowActivatedEvent {
    return { item: event.item, row: event.row, fromDataView: this.fromDataView, sourceIsPagingEvent: this.selectionInProgress };
  }

  public onKeyDown(event: SohoDataGridKeyDownEvent|KeyboardEvent) {
    const keyboardEvent = event as KeyboardEvent;
    const isFilterTarget = (keyboardEvent?.target as HTMLElement)?.classList.contains('btn-filter') &&
      (keyboardEvent?.target as HTMLElement)?.parentElement.classList.contains('datagrid-filter-wrapper');

    if (!this.lmListUtilService.isAutoFilterEnabled() && isFilterTarget && keyboardEvent.which === 13) {
      this.sohoDataGridComponent.applyFilter();
      return false;
    } else {
      this.lmListEventService.listKeyDownEvent(event as LMListKeyDownEvent);
    }
  }

  public onRowClicked(event: SohoDataGridRowClicked) {
    // handle if a form button is clicked off of a form button
    if (!event) {
      return;
    }

    const eventTarget = event.originalEvent.target as HTMLElement;
    const isCellButton = (eventTarget.nodeName === 'BUTTON' || eventTarget.classList.contains('lm-list-icon-button')) &&
      !eventTarget.classList.contains('datagrid-expand-btn');
    const isHyperlink = eventTarget.classList.contains('hyperlink');
    const isCheckBox = eventTarget.classList.contains('datagrid-checkbox');

    if (isCellButton || isHyperlink || isCheckBox) {
      return;
    }

    if (this.listSpec.busListSpec.selection === 'disabled' && !this.listSpec.busListSpec.openActionRestricted) {
      // only fire row click event if the open action is not restricted and selection is 'disabled'.
      // 'disabled' this essentially means a single click is available to open a record.
      if (isNaN(event.cell)) {
        // if the event is not a cell click event
        return;
      }

      this.lmListEventService.listRowClickedEvent(<LMListRowClickedEvent>event);
    } else if (this.lmContextService.useSingleSelectList() && this.listSpec.listType === 'select' && !this.inSelectListEditMode) {
      const isExpandButton = eventTarget.classList.contains('datagrid-expand-btn');
      if (isExpandButton) {
        return;
      }

      if (this.lmListSpecUtilService.isTreeView(this.listSpec)) {
        event.item = this.getDataView(event.row);
      }

      this.lmListEventService.listRowClickedEvent(<LMListRowClickedEvent>event);
    } else if (this.listSpec.busListSpec.selection === 'multiple') {
      /**
       * Fix for issue LMCLIENT-29260.
       *
       * BACKGROUND: We are using the activatedRow event to also select that activated row. But we don't auto select
       * the first row anymore on initial load. A multiselect list will now default so having a zeroth activated row but no row
       * will be selected.
       *
       * ISSUE: since first row is activated (and not selected) by default a new activated event will not be fired
       * if the user clicks on the activated row. There for no selection will take place.
       *
       * WORKAROUND: Manage the click event ourselves and decide if the setRowSelection should be called.
       */
      if (this.activatedRow() === event.row && this.getSelectedRows().length === 0) {
        this.setSelectedRows([ event.row ]);
      }
    }
  }

  public onRowDoubleClicked(event: SohoDataGridRowClicked): void {
    // Safety if double click on filter row and no event is returned
    if (!event) {
      return;
    }
    this.lmListEventService.listRowDoubleClickedEvent(<LMListRowClickedEvent>event);
  }

  public onContextMenu(event: SohoDataGridRowClicked): void {
    // clicked in open area of datagrid. could bring up only class level actions (create or set actions).
    if (!event) {
      return;
    }

    if (!event.originalEvent.clientX || !event.originalEvent.clientY) {
      const domRect: DOMRect = (event.originalEvent.target as HTMLElement).getBoundingClientRect();
      event.originalEvent.clientX = domRect.x;
      event.originalEvent.clientY = domRect.y;
    }

    this.lmListEventService.contextMenuEvent(<LMContextMenuEvent>{
      ...event,
      type: 'grid'
    });
  }

  public onRowAdd(event: SohoDataGridAddRowEvent): void {
    if (!event || !event.value || !event.value.isNewRow) {
      return;
    }

    const columns = this.sohoDataGridComponent.getColumns();
    if (columns && columns[ 0 ].id !== 'selectionCheckbox') {
      return;
    }

    const cell = this.firstInputCellIndex(AppConstants.INLINE_CREATE_ROW);
    if (cell !== -1) {
      this.sohoDataGridComponent.setActiveCell(AppConstants.INLINE_CREATE_ROW, cell);
      this.sohoDataGridComponent.cellNode(AppConstants.INLINE_CREATE_ROW, cell, true).click();
    }
  }

  public firstInputCellIndex(row: number): number {
    const column = this.firstInputColumn(row);
    if (column) {
      // temporary until SOHO fixes getColumnIndex
      // let cellIndex = this.sohoDataGridComponent.getColumnIndex(column.id);
      const columns = this.sohoDataGridComponent.getColumns();
      for (let i = 0; i < columns.length; i++) {
        if (columns[i].id === column.id) {
          return i;
        }
      }
    }

    return -1;
  }

  public firstInputColumn(row: number): ListGridColumn {
    const columns = this.sohoDataGridComponent.getColumns();

    const column = columns.find(col => {
      if (!col.displayField || !col.editor) {
        return false;
      }

      if (col.displayField.type === AppConstants.CHECKCONTROL) {
        return false;
      }

      if (!col.displayField.columnVisibility) {
        return true;
      }

      if (col.displayField.columnVisibility) {
        return this.lmConditionUtilService.isValid(col.displayField.columnVisiblity, this.getDataViewSet().header);
      }
    });

    return column;
  }

  isTimeZoneHasDependentFields(fieldId: string): boolean {
    return !!this.timeZoneDependentFields.find(fields => fields[fieldId]?.length);
  }

  public onCellChange(event: SohoDataGridCellChangeEvent) {
    if (!event) {
      return;
    }

    const columns = this.sohoDataGridComponent.getColumns();
    const columnDef = columns[event.cell];
    const row = event.row;
    const cell = event.cell;
    const dataView: LMDataView = this.getDataViewSet().data[row];
    const dvField: LMField = dataView.fields[columnDef.fieldId] ? dataView.fields[columnDef.fieldId] : dataView.dataView.fields[columnDef.fieldId];

    if (this.lmFormatUtilService.isCompoundField(dvField)) {
      // Needs to be performed before calling rowStatus() since the
      // dataView changes are overwritten.
      this.lmCompoundFieldService.setGroupFieldMemberValue(dataView, dvField);
    }

    if (this.lmListSpecUtilService.isCubeView(this.listSpec)) {
      dvField.oldValue = event.oldValue;
    }

    if (this.isTimeZoneHasDependentFields(columnDef.displayField?.fieldId)) {
      const transformOptions: LMTransformOptions = {};
      const timeZoneDependentField = this.timeZoneDependentFields.find(field => field[columnDef.displayField.fieldId]);

      if (timeZoneDependentField[columnDef?.fieldId]) {
        const timeZoneDependentFields = [ ...new Set(timeZoneDependentField[columnDef.fieldId]) ];
        timeZoneDependentFields.forEach(field => {
          const timeStampfield = dataView.fields[field];
          transformOptions.timeZone = event.oldValue;
          const displayValue = this.lmFormatUtilService.toDisplayValue(
            timeStampfield.value, AppConstants.TIMESTAMP, transformOptions);

          transformOptions.timeZone = event.value;
          timeStampfield.value = this.lmFormatUtilService.toDataValue(
            displayValue, AppConstants.TIMESTAMP, transformOptions);
        });
      }
    }

    // check control is NOT a field it can not be dirty, it is a toggle button
    if (columnDef.displayField.type === AppConstants.CHECKCONTROL) {
      this.sohoDataGridComponent.clearDirtyCell(row, cell);
    }

    if (this.isRowDirty(dataView)) {
      this.sohoDataGridComponent.rowStatus(row, 'in-progress', Soho.Locale.translate('RowDataChanged'));
    } else {
      this.sohoDataGridComponent.clearRowError(row);
    }

    if (columnDef.displayField.type === AppConstants.CHECKCONTROL) {
      this.lmListEventService.checkControlEvent(<LMCheckControlEvent>{ columnDef, dataView, row });
    } else {
      this.lmListEventService.fieldChangedEvent(<LMListFieldChangedEvent>{ columnDef, dataView });
    }
  }

  private onExpandableRow(): SohoDataGridExpandRowFunction {
    // @ts-ignore
    return ((
      eventData: { detail: HTMLElement, row: number, item: LMDataView, grid: SohoDataGridStatic },
      response: SohoDataGridExpandRowResponseFunction
    ) => {
      const columnDef: ListGridColumn = { displayField: this.listSpec.busListSpec.expandableRowCardView };

      columnDef.automationId = this.listSpec.busListSpec.automationId;

      const card = this.lmListGridBuilderService.LMCardViewFormatter(
        eventData.row,
        null,
        null,
        columnDef,
        eventData.item,
        eventData.grid
      );

      // Bind click event to row for card view
      if (eventData.detail) {
        eventData.detail[0].addEventListener('click', (event: MouseEvent) => {
          this.onColumnClicked(columnDef, event, [ {
            originalEvent: null,
            row: eventData.row,
            cell: null,
            item: eventData.item
          } ]);
        });
      }

      response(`${card}`);
    });
  }

  public onColumnClicked(columnDef: ListGridColumn, event: MouseEvent, args: SohoDataGridColumnClickData[]) {

    const row = args[0].row;
    const cell = args[0].cell;
    const dataView = args[0].item;
    const target = event.target as HTMLElement;
    const isCardButton = target.classList.contains('lm-card-button');
    const isCardLinkNavigation = target.classList.contains('lm-card-internal-link');

    // Get displayfield info from element
    // Card button displayfield is a buttonRow with an Array of display fields
    if (isCardButton) {
      const displayField = JSON.parse(target.dataset.displayfield);
      const cardViewFormButtonSpec = displayField.cardViewFormButtonSpec;

      // Check if it is an external button link displayed as text, no icon, and no http resource value
      // if it is, it should not propagate the button event.
      if (cardViewFormButtonSpec
        && cardViewFormButtonSpec.displayAsLink
        && !cardViewFormButtonSpec.iconUrl
        && cardViewFormButtonSpec.openExternal
        && (cardViewFormButtonSpec.httpUrl
          && !cardViewFormButtonSpec.httpUrl.variables)) {
        return;
      }

      this.lmListEventService.toolbarButtonEvent({
        event,
        actionReference: displayField,
        customToolbarButtonInfo: cardViewFormButtonSpec
      });
    } else {
      let navigationParams: LMCardNavigationParams = null;

      if (target.dataset?.navigationName) {
        navigationParams = { navigationName: target.dataset.navigationName, dataView };
      }

      if (isCardLinkNavigation) {
        const cardLinkFieldId = target.dataset.fieldId;
        const cardViewDisplayFields: Array<LMListDisplayField> = [
          ...(columnDef?.displayField?.mainColumn || []),
          ...(columnDef?.displayField?.rightColumn || [])
        ];

        const cardLinkDisplayField: LMListDisplayField = cardViewDisplayFields.find(displayField => displayField.fieldId === cardLinkFieldId);
        const tmpColDef: ListGridColumn = { ...columnDef };
        tmpColDef.displayField = cardLinkDisplayField;

        this.onFormButtonClicked(tmpColDef, dataView, row, cell, navigationParams, event);
      } else {
        this.onFormButtonClicked(columnDef, dataView, row, cell, navigationParams, event);
      }
    }
  }

  protected onCloseFilterRow(event: SohoDataGridOpenFilterRowEvent) {
    this.listState.codeBlockFilterCriteria = [];
    if (this.listState.codeBlockFilterDataView) {
      delete this.listState.codeBlockFilterDataView;
    }
    if (this.listState.userListViewItem) {
      delete this.listState.userListViewItem;
    }
  }

  protected onOpenFilterRow(event: SohoDataGridOpenFilterRowEvent) {
    if (!this.listState.userListViewItem) {
      // set the columns default filter operator
      const filterCriteria = this.getDefaultOperatorFieldCriteria();
      this.m_filterCriteria = filterCriteria;
      this.listState.filterCriteria = filterCriteria;
      this.updateFilterBar();
    }
  }

  protected onFormButtonClicked(columnDef: ListGridColumn, dataView: LMDataView, row: number, cell: number, navigationParams: any, event: any, fromNoDataMessageFormButton?: boolean) {
    // Handle click of the no data message button or button click from a datagrid cell
    if (columnDef.displayField?.linkNavigation ||
        columnDef.displayField?.navigation ||
        columnDef.displayField?.httpUrl ||
        navigationParams
    ) {
      const uiLinkEvent: LMUILinkEvent = {
        columnDef,
        dataView,
        event,
        navigationParams,
        options: { fromNoDataMessageFormButton }
      };
      this.lmListEventService.uiLinkEvent(uiLinkEvent);
    } else if (columnDef.displayField.type === AppConstants.FORMBUTTON ||
        columnDef.displayField.type === AppConstants.SUBFORM ||
        columnDef.displayField.type === AppConstants.FORMREFERENCE ||
        columnDef.displayField.type === AppConstants.LISTREFERENCE) {
      this.lmListEventService.formButtonEvent(<LMFormButtonEvent>{ columnDef, dataView, row, cell, event });
    }
  }

  public onSettingsChanged(event: SohoDataGridSettingsChangedEvent) {
    const columns = event.columns as ListGridColumn[];
    this.lmListEventService.settingsChangedEvent({ columns });
  }

  public onFilterOperatorChanged(event: SohoDataGridFilterOperatorChangedEvent) {
    const selectedOperator = AppConstants.OperatorMap.find(operatorEntry => operatorEntry.sohoOperator === event.operator).lmOperator;
    // const defaultOperator = AppConstants.OperatorMap.find(operatorEntry => operatorEntry.sohoOperator === event.defaultOperator).lmOperator;

    this.lmListEventService.filterOperatorChangedEvent({
      id: event.columnId,
      operator: selectedOperator,
      // defaultOperator: defaultOperator
    });
  }

  public onRendered(event: SohoDataGridRenderedEvent) {
    // onRendered is not called anymore
  }

  /***********************************************************************
   * PUBLIC FUNCTIONS
   ***********************************************************************/

  public getColumns(): Array<ListGridColumn> {
    if (this.sohoDataGridComponent) {
      return this.sohoDataGridComponent.getColumns();
    }
  }

  public toggleFilterRow(): void {
    if (this.sohoDataGridComponent) {
      // ----------------------------------------------------------------
      // if filter bar is currently displayed then clear the filter
      // running clearFilter will cause a 'filtered' message to
      // the this.dataGridSource function.
      // ----------------------------------------------------------------
      if (this.sohoDataGridComponent.filterable) {
        this.sohoDataGridComponent.clearFilter();
      }

      this.sohoDataGridComponent.toggleFilterRow();
      this.listState.isFilterBarOpened = this.sohoDataGridComponent.filterable;
    }
  }

  public getDataViewSet(): LMDataViewSet {
    return this.dataViewSet;
  }

  public getDataView(row: any) {
    if (!this.dataViewSet || this.dataViewSet.data.length <= 0) {
      return null;
    }
    return this.dataViewSet.data[ row ];
  }

  public clearDirty() {
    this.sohoDataGridComponent?.clearDirty();
  }

  public updateDataView(key: number, dataView: LMDataView) {
    this.sohoDataGridComponent.updateRow(key, dataView);
  }

  protected inSelectedListEditModeChanged(): void {
    if (!this.sohoDataGridComponent) {
      return;
    }

    let row: number;
    const fromSelectionMode = this.sohoDataGridComponent.gridOptions.selectable;
    const toSelectionMode = this.lmListSpecUtilService.getSelectionMode(this.listSpec, this.inSelectListEditMode, this.allowMultiSelections);
    if (toSelectionMode !== fromSelectionMode) {
      this.sohoDataGridComponent.gridOptions.selectable = toSelectionMode;

      const showSelectionColumn = toSelectionMode === 'mixed';

      this.selectionInProgress = true;
      row = (showSelectionColumn) ? this.getSelectedRow() : this.activatedRow();

      this.showSelectionCheckBoxColumn(showSelectionColumn);

      // TODO: this code can be removed when the select all checkbox is not shown in selection mode
      if (this.allowMaintainingMultiSelections && !this.inSelectListEditMode) {
        this.doNotClearSelectionTags = true;
      }
      this.sohoDataGridComponent.unSelectAllRows();

      if (showSelectionColumn) {
        // Going from single select to multiple select
        this.sohoDataGridComponent.gridOptions.disableRowDeactivation = true;
        let performActivateRow = true;
        if (this.allowMaintainingMultiSelections && !this.inSelectListEditMode) {
          this.selectRowsBySelectedValues();
          if (this.lmListSpecUtilService.isTreeView(this.listSpec)) {
            // For a tree list do not activate row if it's not in the maintained selected values
            // since it selects the row, which will then adds it to the selection tags
            const fieldId = this.selectRequestData.selectedValuesData.fieldId;
            const dataView = this.getDataView(row);
            performActivateRow = this.multiSelectedValues.some(item => item.formattedValue === dataView.fields[fieldId]?.value);
          }
        }
        if (performActivateRow) {
          this.sohoDataGridComponent.activateRow(row);
        }
      } else {
        // Going from multiple select to single select
        this.sohoDataGridComponent.gridOptions.disableRowDeactivation = false;
        this.sohoDataGridComponent.deactivateRow();
        this.sohoDataGridComponent.selectRows([ row ]);
      }
      this.selectionInProgress = false;
    } else {
      if (!this.allowMultiSelections) {
        row = (this.getSelectedRow() > -1) ? this.getSelectedRow() : 0;
        this.sohoDataGridComponent.selectRows([ row ]);
      } else {
        if (this.allowMaintainingMultiSelections) {
          // TODO: this code can be removed when the select all checkbox is not shown in selection mode
          if (!this.inSelectListEditMode) {
            this.doNotClearSelectionTags = true;
          }
          this.sohoDataGridComponent.unSelectAllRows();
          this.showSelectAllCheckBox(this.inSelectListEditMode);
          if (!this.inSelectListEditMode) {
            this.selectRowsBySelectedValues();
          }
        }
        row = (this.activatedRow() > -1) ? this.activatedRow() : 0;
      }
    }

    // When cellNavigation is false get the hand cursor; otherwise get the pointer cursor
    this.sohoDataGridComponent.gridOptions.cellNavigation = !this.sohoDataGridComponent.gridOptions.cellNavigation;

    setTimeout( () => { this.setActiveCell(row, 0); }, 2);
  }

  protected showSelectionCheckBoxColumn(showSelectionColumn: boolean) {
    const columns: SohoDataGridColumn[] = this.sohoDataGridComponent.gridOptions.columns;
    this.lmListGridBuilderService.updateDataGridColumn(this.listSpec, columns, showSelectionColumn);
    this.sohoDataGridComponent.columns = columns;

    if (this.allowMaintainingMultiSelections && showSelectionColumn) {
      this.showSelectAllCheckBox(this.inSelectListEditMode);
    }

    const frozenColumns: SohoDataGridFrozenColumns = this.sohoDataGridComponent.gridOptions.frozenColumns;
    if (frozenColumns?.left.length > 0) {
      this.lmListGridBuilderService.updateFrozenColumns(frozenColumns, showSelectionColumn);
      this.sohoDataGridComponent.frozenColumns = frozenColumns;
    }

    const columnGroups: SohoDataGridColumnGroup[] = this.sohoDataGridComponent.gridOptions.columnGroups;
    if (columnGroups?.length > 0) {
      this.lmListGridBuilderService.updateColumnGroups(columnGroups, showSelectionColumn);
      this.sohoDataGridComponent.columnGroup = columnGroups;
    }
  }

  public showSelectAllCheckBox(showCheckBoxInHeader: boolean): void {
    // noop
  }

  public multiSelectedValuesChanged(): void {
    this.selectRowsBySelectedValues();
  }

  protected selectRowsBySelectedValues(): void {
    if (!this.sohoDataGridComponent) {
      return;
    }

    if (!this.allowMaintainingMultiSelections || this.inSelectListEditMode) {
      return;
    }

    const rowIndexes: number[] = this.selectedValuesToRowIndexes();
    if (rowIndexes.length > 0) {
      this.sohoDataGridComponent.selectRows(rowIndexes);
    }
  }

  protected selectedValuesToRowIndexes(): number[] {
    const rowIndexes = [];
    const dataViewSet: LMDataViewSet = this.getDataViewSet();
    for (const multiSelectedValue of this.multiSelectedValues) {
      dataViewSet.data.filter((dataView, index) => {
        if (dataView.fields[multiSelectedValue.fieldId]?.value === multiSelectedValue.formattedValue) {
          rowIndexes.push(index);
          return true;
        }
      });
    }
    return rowIndexes;
  }

  public updateDataViewTransaction(key: number, status: string, message: string): void {
    // depending on the the status,
    // Complete - row marked pristine and successful
    // else - row marked in error
    // TODO how do we handle transactions that have bee canceled because
    // the user did not confirm the action or did not enter the required input
    // The cancelled transaction will have status AppConstants.CONFIRMATION_REQUIRED

    const rowStatus = AppConstants.STATUS_COMPLETED === status ? '' : 'error';
    this.rowStatus(key, rowStatus, message);
  }

  public activateRow(row: number, scrollIntoView?: boolean): void {
    if (this.listSpec.busListSpec.selection === 'disabled') {
      return; // safety
    }

    // todo: deal with when the row is greater than the number of rows.
    this.sohoDataGridComponent.activateRow(row);
  }

  public activatedRow(): number {
    if (!this.sohoDataGridComponent || !this.sohoDataGridComponent.activatedRow()) {
      return -1;
    }
    const activatedRowResult = this.sohoDataGridComponent.activatedRow();
    return (activatedRowResult instanceof Array) ? activatedRowResult[ 0 ].row : activatedRowResult.row;
  }

  public get sohoFilterConditions(): Array<SohoDataGridFilterCondition> {
    return this.sohoDataGridComponent.filterConditions();
  }

  public get lmFilterCriteria(): Array<LMFilterFieldCriteria> {
    return this.getFilterCriteria(this.sohoFilterConditions);
  }

  public isModified(): boolean {
    return (this.getDirtyRows().length > 0);
  }

  public isRowDirty(dataView: LMDataView) {
    const dirtyDataViews: Array<LMDataView> = this.getDirtyRows(true);
    return dirtyDataViews.includes(dataView);
  }

  public getDirtyRows(commitEdits: boolean = undefined): Array<LMDataView> {
    return this.sohoDataGridComponent.dirtyRows();
  }

  // eslint-disable-next-line @typescript-eslint/ban-types
  public setDirtyIndicator(row: number, cell: number, showDirty: boolean, data?: Object): void {
    this.sohoDataGridComponent.setDirtyIndicator(row, cell, showDirty, data);
  }

  public onContextMenuItemSelected(actionReference: LMBusActionReference, selectedDataView?: LMDataView): void {
    // TODO
  }

  public disableSaveInUserListViewMenu(): boolean {
    // TODO: The saved search in datagrid depends on filter criteria.
    // TODO: Enable or disable based on sort order that is part of LMSearchState.
    return !this.listState.filterCriteria?.length;
  }

  public addDataView(inlineCreateDataView: LMDataView, row: number) {
    this.sohoDataGridComponent.addRow(inlineCreateDataView, row);
  }

  public removeDataView(row: number) {
    this.setSelectedRows([ row ]);
    this.sohoDataGridComponent.removeSelected();
  }

  public removeRow(row: number) {
    this.sohoDataGridComponent.removeRow(row);
  }

  public rowStatus(row: number, status: string, tooltip: string) {
    // Need to remove any existing row state, otherwise row
    // status indicator is not changed
    this.sohoDataGridComponent.clearRowError(row);
    this.sohoDataGridComponent.rowStatus(row, status, tooltip);
    if (status === '') {
      this.sohoDataGridComponent.clearDirtyRow(row);
    }
  }

  public setActiveCell(row: number, cell: number) {
    this.sohoDataGridComponent.setActiveCell(row, cell);
  }

  public updateRow(idx: number, row: any) {
    this.sohoDataGridComponent.updateRow(idx, row);
  }

  public setSelectedRows(rows: Array<number>, scrollIntoView?: boolean): void {
    if (this.listSpec.busListSpec.selection === 'disabled') {
      return; // safety
    }
    this.sohoDataGridComponent.selectRows(rows);
  }

  public unSelectAllRows(): void {
    if (this.listSpec.busListSpec.selection === 'disabled') {
      return;
    }

    this.sohoDataGridComponent.unSelectAllRows();
  }

  public getSelectedRows(): Array<number> {
    if (this.listSpec.busListSpec.selection === 'disabled') {
      return [];
    }

    const dataGridSelectedRows: SohoDataGridSelectedRow[] = this.sohoDataGridComponent.selectedRows();
    return dataGridSelectedRows.map(dataGridSelectedRow => dataGridSelectedRow.idx);
  }

  public getSelectedRow(): number {
    const selectedRows = this.getSelectedRows();
    return selectedRows && selectedRows.length > 0 ? selectedRows[ 0 ] : -1;
    // the code below break matrix when it has more than one row selected
    // the matrix uses the first selected row'd resource id to do a navigation
    // if (selectedRows && selectedRows.length === 1) {
    //   return selectedRows[ 0 ];
    // } else {
    //   return -1;
    // }
  }

  public columnById(id: string) {
    const columns: Array<ListGridColumn> = this.sohoDataGridComponent.columnById(id);
    return (columns.length > 0) ? columns[0] : null;
  }

  public commitCellEdit(): void {
    this.sohoDataGridComponent.commitCellEdit();
  }

  public clearFilter(): void {
    this.sohoDataGridComponent.clearFilter();
  }

  public applyFilter(): void {
    this.sohoDataGridComponent.applyFilter();
  }

  private filterCriteriaForRequiredSearchField(filterCriteria: LMFilterFieldCriteria, requiredFieldId: string): boolean {
    if (filterCriteria.fieldId === requiredFieldId) {
      // if field allows multiple search terms ensure array isn't empty
      if (Array.isArray(filterCriteria.value)  && filterCriteria.value.length > 0) {
        return true;
      }
      if (String(filterCriteria.value)?.length > 0) {
        return true;
      }
    }

    if (filterCriteria.isCompactFormatField) {
      return (filterCriteria.fieldId.startsWith(requiredFieldId));
    }

    return false;
  }

  protected validateFilterCriteria(filterCriteria: LMFilterFieldCriteria[]): boolean {
    let isValid = true;
    const requiredFields = [];
    if (filterCriteria && filterCriteria.length > 0 && this.listSpec.busListSpec.requiredSearchParameters) {
      this.listSpec.busListSpec.requiredSearchParameters.forEach(rsp => {
        const requiredField = filterCriteria.find(fc => this.filterCriteriaForRequiredSearchField(fc, rsp.fieldId));

        if (!requiredField) {
          isValid = false;
          requiredFields.push(rsp.fieldId);
        }
      });
    }

    if (!isValid) {
      const title = Soho.Locale.translate('InvalidSearchParameters');
      let fieldId = requiredFields[0];
      const gridColumn = this.getColumns().find(col => col.fieldId === fieldId && col.isRequiredSearch);

      if (gridColumn) {
        fieldId = gridColumn.name;
      }

      const message = this.lmFormatUtilService.formatMessage(Soho.Locale.translate('SearchParameterRequired'), fieldId);
      this.showError(title, message);
    }

    return isValid;
  }

  public filterCriteriaForDefaultFilter(): LMFilterFieldCriteria[] {
    let filterCriteria: LMFilterFieldCriteria[] = [];
    if (!this.lmListUtilService.hasSearchForm(this.listSpec, this.lmListSpecUtilService.isDashBoardContent(this.listSpec))
        && !this.lmListSpecUtilService.hasSearchFields(this.listSpec)
        && this.lmListSpecUtilService.hasAlphaDefaultFilter(this.listSpec)) {
      filterCriteria = this.getDefaultOperatorFieldCriteria();
    }
    return filterCriteria;
  }

  private getDefaultOperatorFieldCriteria(): LMFilterFieldCriteria[] {
    const filterCriteria: LMFilterFieldCriteria[] = [];
    // Default filters can now be per display field, and on alpha, numeric, and date fields
    const columns: ListGridColumn[] = this.sohoDataGridComponent.getColumns();
    columns.filter((column: ListGridColumn) => column.defaultFilterOperator)
      .map((column: ListGridColumn) => {
        const sohoFilterCondition = {
          columnId: column.id,
          operator: column.defaultFilterOperator,
          value: ''
        };
        filterCriteria.push(this.lmListUtilService.buildFilterCriteria(sohoFilterCondition, column, true));
      });
    return filterCriteria;
  }

  protected showError(title: string, message: string) {
    const buttons = [
      {
        id: this.lmUniqueIdService.uniqueId([ 'listGridErrorDlg', LMDialogButtonKey.CLOSE ]),
        text: Soho.Locale.translate('Close'), click: (e, modal) => {
          modal.close(true);
        }
      }
    ];

    this.sohoMessageService
      .error()
      .message(message)
      .buttons(buttons)
      .title(title)
      .open();
  }

  /**
   * Column visibility should be updated before loading data into the grid
   * @param listSpec
   * @param dataViewSet
   */
  protected updateColumnVisibility(listSpec: LMListSpec, dataViewSet: LMDataViewSet) {
    const gridColumns: ListGridColumn[] = this.sohoDataGridComponent.getColumns();
    for (let i = gridColumns.length - 1; i >= 0; i--) {
      const gridColumn: ListGridColumn = gridColumns[ i ];
      const displayField: LMListDisplayField = gridColumn.displayField;
      if (displayField && displayField.columnVisibility) {
        let dataView = dataViewSet.header;

        // for template list, framework does NOT put the visibility conditions in the header dataView
        // so we need to use the first targetView in the list.
        if (this.listSpec.isTemplateList) {
          if (dataViewSet.data && dataViewSet.data.length > 0) {
            dataView = dataViewSet.data[ 0 ].dataView;
          }
        }

        if (this.lmConditionUtilService.isValid(displayField.columnVisibility, dataView)) {
          this.sohoDataGridComponent.showColumn(gridColumn.id);
        } else {
          this.sohoDataGridComponent.hideColumn(gridColumn.id);
        }
      }
    }
  }

  /**
   * Updates the column labels on a datagrid. This must be called before data is loaded into the data
   * grid with the gridOptions.source function. Otherwise the pager bar gets screwed up. Here is the
   * soho ticket for this: https://jira.infor.com/browse/SOHO-6990
   */
  public updateColumnLabels(listSpec: LMListSpec, dataViewSet: LMDataViewSet) {
    const gridColumns: ListGridColumn[] = this.sohoDataGridComponent.getColumns();
    const columnGroups: SohoDataGridColumnGroup[] = this.sohoDataGridComponent.getColumnGroups();
    if (this.lmListUtilService.updateColumnLabels(listSpec, dataViewSet, gridColumns, columnGroups)) {
      // ----------------------------------------------------------
      // only call updateColumns if a column has actually had it's
      // column label changed.
      // ----------------------------------------------------------
      this.sohoDataGridComponent.updateColumns(gridColumns, columnGroups);
    }
  }

  protected getSohoConditions(filterCriteriaArray: LMFilterFieldCriteria[]): SohoDataGridFilterCondition[] {
    const gridColumns: ListGridColumn[] = this.sohoDataGridComponent.getColumns() as ListGridColumn[];
    return filterCriteriaArray
      .filter(filterCriteria => !filterCriteria.isKeywordSearch)
      .map(filterCriteria => this.lmListUtilService.buildSohoCondition(filterCriteria, gridColumns))
      .filter(sohoFilterCondition => sohoFilterCondition != null);
  }

  // todo move to a service. perhaps lmListUtilService or a FitlerCriteria services somewhere.
  protected getFilterCriteria(conditions: SohoDataGridFilterCondition[]): LMFilterFieldCriteria[] {
    let filterCriteria: LMFilterFieldCriteria[] = [];
    conditions.map((condition) => {
      const columnDef: ListGridColumn =  this.sohoDataGridComponent.columnById(condition.columnId)[ 0 ] as ListGridColumn;
      if (!columnDef.displayField.compactFormatByForm) {
        filterCriteria.push(this.lmListUtilService.buildFilterCriteria(condition, columnDef));
      } else {
        filterCriteria = filterCriteria.concat(this.listState.codeBlockFilterCriteria);
      }
    });
    return filterCriteria;
  }

  public getSavedColumns(): void {
    const columnString = this.listPersonalization.columns ? JSON.stringify(this.listPersonalization.columns) : undefined;

    if (columnString) {
      this.savedColumns = this.sohoDataGridComponent.columnsFromString(columnString) as ListGridColumn[];
    }
    const savedColumnsCount = this.savedColumns ? this.savedColumns.length : 0;

    if (savedColumnsCount > 0) {
      if (this.gridOptions.columns.length === savedColumnsCount) {
        // If they have the same length, check to make sure there are no added/removed columns (ie. via personalization/configuration)
        for (let i = 0; i < this.gridOptions.columns.length; i++) {
          if (this.gridOptions.columns[i].id !== this.savedColumns[i]?.id) {
            this.savedColumns = [];
            this.lmListEventService.settingsChangedEvent({ columns: this.savedColumns });
            break;
          }
        }
      } else {
        // If they are different lengths, we know they are no longer valid
        this.savedColumns = [];
        this.lmListEventService.settingsChangedEvent({ columns: this.savedColumns });
      }
    }
  }

  protected updateFilterBar() {
    if (this.lmListSpecUtilService.isCubeView(this.listSpec)) {
      // Cube has a default filter dialog if child hierarchy dimensions are present but no search form is defined.
      // But it does not use SohoDataGrid filter bar, safe to return.
      return;
    }
    // ----------------------------------------------------------------------------
    // apply filter criteria only if the soho-datagrid filter bar is being used.
    // ----------------------------------------------------------------------------
    if (this.listState.filterCriteria && !this.listSpec.busListSpec.searchForm && !this.listSpec.busListSpec.searchFields) {
      const conditions: Array<SohoDataGridFilterCondition> = this.getSohoConditions(this.listState.filterCriteria);
      const tempCond = conditions.filter((cond: SohoDataGridFilterCondition) => {
        if (Array.isArray(cond.value)) {
          return true;
        } else {
          return cond.value?.length > 0;
        }
      });

      if (tempCond.length === 0) {
        // No conditions with values exist, call setFilterConditions so that the filter types are set correctly. This
        // would be the case of navigation back to the list or opening the filter row
        this.sohoDataGridComponent.setFilterConditions(conditions);
      }

      const sohoConditions = this.sohoDataGridComponent.filterConditions();

      if (tempCond.length !== sohoConditions.length) {
        // conditions have changed in the list state filter conditions compared to the soho datagrid conditions,
        // update the sohoDataGridComponent
        this.sohoDataGridComponent.setFilterConditions(conditions);
      } else {
        // loop thru to see if the conditions on the listState filter criteria match the sohoDataGridComponent.
        // only call setFilterConditions if they don't match
        let resetDataGridConditions = false;
        tempCond.forEach((cond: SohoDataGridFilterCondition) => {
          if (!this.containsFilterConditions(cond, sohoConditions))  {
            resetDataGridConditions = true;
            return;
          }
        });

        if (resetDataGridConditions) {
          this.sohoDataGridComponent.setFilterConditions(conditions);
        }
      }
    }
  }

  containsFilterConditions(cond: SohoDataGridFilterCondition, otherConditions: Array<SohoDataGridFilterCondition>): boolean {
    let containedConds = otherConditions.filter((oc: SohoDataGridFilterCondition) => cond.columnId === oc.columnId);
    containedConds = containedConds.filter((oc: SohoDataGridFilterCondition) => cond.operator === oc.operator);
    containedConds = containedConds.filter((oc: SohoDataGridFilterCondition) =>{
      if (Array.isArray(cond.value)) {
        if (Array.isArray(oc.value)) {
          return isEqual(cond.value, oc.value);
        } else {
          // values aren't both arrays, they don't match
          return false;
        }
      } else {
        return cond.value === oc.value;
      }
    });

    return containedConds.length > 0;
  }
}
